import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { IonBadge, IonCol, IonGrid, IonIcon, IonItem, IonLabel, IonRow } from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { EUserRole, IUser } from '@okcargo-apps/shared-domain';
import { AccountService } from '../../account';
import { UserDeleteActionComponent } from '../user-delete-action/user-delete-action.component';
import { UserEditActionComponent } from '../user-edit-action/user-edit-action.component';
import { canManageTargetRoles } from '../user-role.utils';
import { USER_ROLE_OPTIONS } from '../users-store.model';
import { UsersStoreService } from '../users-store.service';

@Component({
  selector: 'lib-user-item',
  templateUrl: './user-item.component.html',
  styleUrls: ['./user-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonIcon,
    IonCol,
    IonRow,
    IonGrid,
    IonItem,
    IonLabel,
    IonBadge,
    TranslateModule,
    UserEditActionComponent,
    UserDeleteActionComponent,
  ],
})
export class UserItemComponent {
  public readonly user = input.required<IUser>();

  private readonly accountService = inject(AccountService);
  private readonly usersStoreService = inject(UsersStoreService);

  /** True when this user is the account of the current app session. */
  public readonly isCurrentUser = computed<boolean>(() => {
    const account = this.accountService.account();
    const user = this.user();
    return !!user.id && (user.id === account.contactId || user.id === account.id);
  });

  /**
   * The item owns the user: it decides whether it can be removed. The permission to manage users in
   * general is already guaranteed by the page/route, so it is not re-checked here.
   *
   * A user that holds roles the active user cannot manage must not be deleted nor disabled.
   */
  public readonly canRemove = computed<boolean>(() =>
    canManageTargetRoles(this.usersStoreService.manageableRoles(), this.user().roles),
  );

  public roleLabelKey(role: EUserRole): string {
    return USER_ROLE_OPTIONS.find((option) => option.role === role)?.labelKey ?? '';
  }
}
